/**
 * <WorkloadCard> — single-card health summary for one Deployment / STS / DS.
 *
 * Operator workload card grid (#234). Extended in #236 to render an
 * EventOperator-gated "Restart" button on workloads that carry the
 * `jeket.com/show-affecting` annotation. The aggregator backend at
 * GET /cluster/:siteId/workloads computes `healthClass`, `dominantReason`,
 * `recentRestarts`, and `showAffecting`; this component renders them with
 * the cluster theme's status palette.
 *
 * Restart UX is intentionally minimal:
 *   - Confirmation via window.confirm (matches Events.tsx convention).
 *   - "Restarting…" badge + disabled button for ~5s after click, self-clears
 *     so the next workload poll (5s in WorkloadsPanel) can take over.
 *   - alert() on failure — no toast library is wired in this codebase yet.
 */
import { useCallback, useState } from 'react';
// `@jeket-cloud/types` compiles to CommonJS — Rollup's named-export static
// analysis can't see the runtime `Role` enum through the CJS wrapper. Use
// the namespace import + destructure pattern (same workaround AuthContext
// and RoleGate use).
import { Role } from '@jeket-cloud/types/auth';
import type { Workload } from '@/types';
import { clusterApi } from '@/api/client';
import RoleGate from '@/components/auth/RoleGate';

interface Props {
  workload: Workload;
  /**
   * Site UUID for the cluster this workload lives in. Required to render the
   * restart button — when omitted (legacy callers), the button is hidden.
   */
  siteId?: string;
  /**
   * Click handler — wires the card up to the parent-owned WorkloadDrawer
   * (#241). When omitted the card stays static (the original #234 shape).
   *
   * The Restart button (#236) calls `stopPropagation()` so its click does
   * NOT bubble up and open the drawer; the operator wants those two
   * affordances to be independent.
   */
  onClick?: (workload: Workload) => void;
}

const HEALTH_BADGE_CLASS: Record<Workload['healthClass'], string> = {
  healthy: 'text-status-healthy bg-status-healthy/10',
  degraded: 'text-status-warning bg-status-warning/10',
  failing: 'text-status-error bg-status-error/10',
};

const HEALTH_DOT_CLASS: Record<Workload['healthClass'], string> = {
  healthy: 'bg-status-healthy',
  degraded: 'bg-status-warning',
  failing: 'bg-status-error',
};

/**
 * JSS#697 — say *why* it is failing.
 *
 * This used to read `Failing — restart x{n}` off a lifetime restart total, so
 * a workload that had been Running and Ready for days still read as failing.
 * Now `recentRestarts` only counts the last hour, which also means it can be 0
 * on a genuinely failing workload (nothing Ready, or a crash loop with no
 * dated restart) — "restart x0" would be worse than the original. Lead with
 * the reason that actually applies.
 */
function restartPhrase(n: number): string {
  return `${n} restart${n === 1 ? '' : 's'} in the last hour`;
}

function subtitle(w: Workload): string {
  const reason = w.dominantReason ? ` — ${w.dominantReason}` : '';
  if (w.healthClass === 'failing') {
    if (w.ready === 0 && w.desired > 0) return `Failing 0/${w.desired}${reason}`;
    if (w.recentRestarts > 0) return `Failing — ${restartPhrase(w.recentRestarts)}${reason}`;
    return `Failing ${w.ready}/${w.desired}${reason}`;
  }
  if (w.healthClass === 'degraded') {
    // Partially available reads as a count; fully available but recently
    // restarted reads as a rate. Both are degraded, for different reasons.
    if (w.ready < w.desired) return `Degraded ${w.ready}/${w.desired}${reason}`;
    return `Degraded — ${restartPhrase(w.recentRestarts)}${reason}`;
  }
  return `Healthy ${w.ready}/${w.desired}`;
}

const KIND_PATH: Record<Workload['kind'], 'deployment' | 'statefulset' | 'daemonset'> = {
  Deployment: 'deployment',
  StatefulSet: 'statefulset',
  DaemonSet: 'daemonset',
};

// Self-clear timeout for the "Restarting…" hint. Slightly longer than the
// WorkloadsPanel poll interval (5s) so the next aggregator response — which
// will reflect the new pod template — is what actually clears it in practice.
const RESTARTING_HINT_MS = 6000;

export default function WorkloadCard({ workload, siteId, onClick }: Props) {
  const badgeClass = HEALTH_BADGE_CLASS[workload.healthClass];
  const dotClass = HEALTH_DOT_CLASS[workload.healthClass];
  const [restarting, setRestarting] = useState(false);

  const handleCardClick = useCallback(() => {
    onClick?.(workload);
  }, [onClick, workload]);

  const handleRestart = useCallback(async (e: React.MouseEvent) => {
    // Don't bubble up to the card's onClick — operators expect Restart to
    // act in-place without also popping the drawer.
    e.stopPropagation();
    if (!siteId) return;
    const confirmed = window.confirm(
      `Restart ${workload.name}? In-flight viewers will reconnect.`,
    );
    if (!confirmed) return;

    setRestarting(true);
    // Auto-clear so a stuck request (or a quick aggregator poll lag) doesn't
    // leave the badge frozen. The actual rollout takes longer than 6s in
    // practice, but the visual state is meant to be a "you clicked it"
    // acknowledgement, not a rollout progress indicator.
    const clearTimer = window.setTimeout(() => setRestarting(false), RESTARTING_HINT_MS);

    try {
      await clusterApi.restartWorkload(
        siteId,
        workload.namespace,
        KIND_PATH[workload.kind],
        workload.name,
      );
      // Success: leave the badge in place for the duration of the timer so
      // the operator sees a clear ack. No toast library available yet.
    } catch (err) {
      window.clearTimeout(clearTimer);
      setRestarting(false);
      const msg = err instanceof Error ? err.message : String(err);
      window.alert(`Failed to restart ${workload.name}: ${msg}`);
    }
  }, [siteId, workload.kind, workload.name, workload.namespace]);

  const showRestart = workload.showAffecting && Boolean(siteId);
  const isClickable = Boolean(onClick);

  return (
    <div
      data-testid="workload-card"
      data-health={workload.healthClass}
      onClick={isClickable ? handleCardClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={
        isClickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
      className={`bg-surface border border-surface-elevated rounded-xl p-4 space-y-2 hover:border-gold/30 transition-colors ${
        isClickable ? 'cursor-pointer focus:outline-none focus:border-gold/60' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-silver-bright truncate">{workload.name}</div>
          <div className="text-xs text-silver-muted truncate">
            <span className="font-mono">{workload.kind}</span>
            <span className="mx-1">·</span>
            {workload.namespace}
          </div>
        </div>
        <span className={`shrink-0 inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded font-medium ${badgeClass}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`} aria-hidden="true" />
          {workload.healthClass}
        </span>
      </div>
      <div className="text-xs text-silver-muted">
        {subtitle(workload)}
      </div>
      {showRestart && (
        <RoleGate role={Role.EventOperator}>
          <div className="flex items-center justify-between gap-2 pt-1">
            {restarting ? (
              <span
                data-testid="workload-restarting-badge"
                className="text-xs text-status-warning bg-status-warning/10 px-2 py-0.5 rounded font-medium"
              >
                Restarting…
              </span>
            ) : (
              <span className="text-xs text-silver-muted">Show-affecting</span>
            )}
            <button
              type="button"
              data-testid="workload-restart-button"
              onClick={handleRestart}
              disabled={restarting}
              className="text-xs px-2 py-1 rounded border border-gold/30 text-gold hover:border-gold/60 hover:bg-gold/5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {restarting ? 'Restarting…' : 'Restart'}
            </button>
          </div>
        </RoleGate>
      )}
    </div>
  );
}
